/**
 * API волны W5 — аналитика (learning-kpis, фильтры периода/точек), актуальность, проверка ответов (returned + практика), обращения, админ-экраны.
 *
 * Правила (план wk6 §4): lib/wikiApi.ts заморожен после фазы 0 — новые вызовы
 * и аддитивные DTO волна кладёт только в свой модуль. Клиент — `api` из
 * '../api'. Существующие DTO расширять пересечением.
 *
 * Аналитика — только kb:admin (бэк отвечает 403 иначе); проверка ответов —
 * kb:admin или kb:review_attempts (управляющий видит свою команду).
 */
import { api } from '../api';
import type {
  KpiCode,
  LearningEffectCourse,
  ReviewQueueItem,
  VerifyQueueDto,
  VerifyQueueRow,
  WikiNotifPrefs,
  WikiUserDto,
} from '../wikiApi';
import type { PracticalReviewInput, PracticalSubmission } from './learn';

export type KpiCodeT = KpiCode;

// ─── Фильтр аналитики ────────────────────────────────────────────────

/** Период (дни МСК, включительно) + сравнение + точки (пусто — вся сеть). */
export interface AnalyticsQuery {
  from: string;
  to: string;
  cmpFrom?: string;
  cmpTo?: string;
  pointIds?: string[];
}

/** Query-строка фильтра: ?from=&to=&cmpFrom=&cmpTo=&pointIds=a,b (+ доп. параметры). */
export function analyticsQs(q: Partial<AnalyticsQuery>, extra: Record<string, string | number | null | undefined> = {}): string {
  const p = new URLSearchParams();
  if (q.from) p.set('from', q.from);
  if (q.to) p.set('to', q.to);
  if (q.cmpFrom) p.set('cmpFrom', q.cmpFrom);
  if (q.cmpTo) p.set('cmpTo', q.cmpTo);
  if (q.pointIds?.length) p.set('pointIds', q.pointIds.join(','));
  for (const [k, v] of Object.entries(extra)) if (v != null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

// ─── DTO аналитики ───────────────────────────────────────────────────

export interface KpiPair {
  value: number | null;
  prev: number | null;
}

export interface FunnelDto {
  assigned: number;
  started: number;
  completed: number;
  certified: number;
}

export interface LearningKpisDto {
  period: { from: string; to: string };
  compare: { from: string; to: string };
  pointIds: string[];
  kpis: {
    completionPct: KpiPair;
    avgScore: KpiPair;
    activeLearners: KpiPair;
    certificates: KpiPair;
    overdue: KpiPair;
  };
  funnel: FunnelDto;
  funnelPrev: FunnelDto;
}

export interface CompletionsSeriesDto {
  granularity: 'day' | 'week' | 'month';
  series: Array<{ bucket: string; label: string; value: number }>;
  compare: Array<number | null>;
  total: number;
  compareTotal: number;
}

export interface TrainingByRoleRow {
  role: string;
  roleCode: string | null;
  source: 'crm' | 'emp';
  percent: number;
  users: number;
  assigned: number;
  started: number;
  completed: number;
  avgScore: number | null;
}

export interface PointsTreePartner {
  id: string;
  name: string;
  city: string | null;
  points: Array<{ id: string; name: string }>;
}

export interface AnalyticsPointRow {
  tradingPointId: string;
  pointName: string;
  partnerName: string;
  newsReadPct: number;
  educationPct: number;
  standardsPct: number;
  hasData: boolean;
  status: 'ok' | 'warning' | 'overdue' | 'no_data';
  // HUB 2.0 (с фильтром): обучение сотрудников точки.
  city?: string | null;
  partnerId?: string;
  pointLabel?: string;
  learners?: number;
  assigned?: number;
  completed?: number;
  completionPct?: number | null;
  avgScore?: number | null;
  overdue?: number;
  certificates?: number;
}

export interface NetworkOverviewDto {
  reach: number;
  readPct: number;
  eduPct: number;
  compliancePct: number;
  activeUsers: number;
}

export interface ContentAnalyticsDto {
  totalArticles: number;
  totalReads: number;
  topViewed: Array<{ slug: string; title: string; viewCount: number }>;
  topTime: Array<{ slug: string; title: string; totalMin: number }>;
  searchNotFound: Array<{ query: string; count: number }>;
  mostAcked: Array<{ slug: string; title: string; acked: number }>;
  standardsAck?: Array<{ slug: string; title: string; acked: number; audience: number; pct: number }>;
  period?: { from: string; to: string } | null;
}

export type LearningEffectX = LearningEffectCourse & { scoreHistogram?: number[]; passingScore?: number | null };

export interface HardQuestionX {
  questionId: string;
  text: string;
  quizTitle: string | null;
  courseName: string | null;
  courseId?: string | null;
  kind?: string | null;
  answered: number;
  wrong: number;
  wrongPct: number;
}

export interface KpiCorrelationX {
  kpi: KpiCodeT;
  kpiLabel: string;
  days: number;
  groups: { trained: { points: number; avgKpi: number | null }; others: { points: number; avgKpi: number | null } };
  rows: Array<{ tradingPointId: string; pointName: string; educationPct: number; kpi: number | null; group: 'trained' | 'others' }>;
}

export type VerifyQueueX = VerifyQueueDto & {
  counts: VerifyQueueDto['counts'] & { fresh?: number; total?: number };
  fresh?: VerifyQueueRow[];
};

export const analyticsApi = {
  pointsTree: () => api.get<PointsTreePartner[]>('/wiki/admin/analytics/points-tree'),
  learningKpis: (q: AnalyticsQuery) => api.get<LearningKpisDto>(`/wiki/admin/analytics/learning-kpis${analyticsQs(q)}`),
  completions: (q: AnalyticsQuery, granularity?: 'day' | 'week' | 'month') =>
    api.get<CompletionsSeriesDto>(`/wiki/admin/analytics/completions${analyticsQs(q, { granularity })}`),
  overview: (q: AnalyticsQuery) => api.get<NetworkOverviewDto>(`/wiki/admin/analytics/overview${analyticsQs(q)}`),
  trainingByRole: (q: AnalyticsQuery) => api.get<TrainingByRoleRow[]>(`/wiki/admin/analytics/training-by-role${analyticsQs(q)}`),
  points: (q: AnalyticsQuery) => api.get<AnalyticsPointRow[]>(`/wiki/admin/analytics/points${analyticsQs(q)}`),
  learningEffect: (q: AnalyticsQuery, courseId?: string | null) =>
    api.get<LearningEffectX[]>(`/wiki/admin/analytics/learning-effect${analyticsQs(q, { courseId })}`),
  hardQuestions: (q: AnalyticsQuery) => api.get<HardQuestionX[]>(`/wiki/admin/analytics/hard-questions${analyticsQs(q)}`),
  kpiCorrelation: (q: AnalyticsQuery, kpi: KpiCodeT) =>
    api.get<KpiCorrelationX>(`/wiki/admin/analytics/kpi-correlation${analyticsQs(q, { kpi })}`),
  content: (q: Pick<AnalyticsQuery, 'from' | 'to'>) => api.get<ContentAnalyticsDto>(`/wiki/admin/analytics${analyticsQs({ from: q.from, to: q.to })}`),
  verifyQueue: () => api.get<VerifyQueueX>('/wiki/admin/verify-queue'),
  verifyRemind: () => api.post<{ checked: number; reminded: number }>('/wiki/admin/verify/remind', {}),
};

// ─── Проверка ответов (попытки + практика) ───────────────────────────

/** Сдача практики в очереди проверяющего: общий контракт W3 + бриф урока. */
export type PracticalQueueItem = PracticalSubmission & {
  lessonTitle: string;
  task: string | null;
  criteria: string[];
  sample: string | null;
  points: number | null;
  attemptNo: number;
};

/** Попытка в очереди: статус может быть returned (HUB 2.0). */
export type ReviewAttemptItem = ReviewQueueItem;

export interface ReviewsQueueDto {
  attempts: ReviewAttemptItem[];
  practical: PracticalQueueItem[];
}

export const reviewsApi = {
  queue: (status: 'pending' | 'done' | 'all' = 'pending') => api.get<ReviewsQueueDto>(`/wiki/admin/reviews/queue?status=${status}`),
  /** Принять/оценить попытку — прежний эндпоинт конструктора LMS. */
  applyAttempt: (attemptId: string, items: Array<{ questionId: string; score: number; comment?: string | null }>) =>
    api.post<{ status: string; score: number; passed: boolean; courseCompleted: boolean }>(
      `/wiki/admin/lms/reviews/${encodeURIComponent(attemptId)}`,
      { items },
    ),
  returnAttempt: (attemptId: string, comment: string, items?: Array<{ questionId: string; score?: number | null; comment?: string | null }>) =>
    api.post<{ status: 'returned' }>(`/wiki/admin/reviews/attempts/${encodeURIComponent(attemptId)}/return`, { comment, items }),
  reviewPractical: (id: string, input: PracticalReviewInput) =>
    api.post<{ status: 'accepted' | 'returned'; courseCompleted: boolean; lessonCompleted: boolean; warning: string | null }>(
      `/wiki/admin/reviews/practical/${encodeURIComponent(id)}`,
      input,
    ),
};

// ─── Пользователи и уведомления ──────────────────────────────────────

/** Пользователь вики + прогресс обучения (HUB 2.0). */
export type WikiUserX = WikiUserDto & { coursesStarted?: number; coursesCompleted?: number; trainingPct?: number | null };

export const NOTIFY_EVENTS = ['courses', 'reviews', 'standards', 'verify', 'questions', 'mentions', 'opening', 'other'] as const;
export type NotifyEvent = (typeof NOTIFY_EVENTS)[number];
export type NotifyChannels = Partial<Record<NotifyEvent, { push?: boolean }>>;
export type WikiNotifPrefsX = WikiNotifPrefs & { channels?: NotifyChannels };

export const adminUsersApi = {
  list: (params: { search?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.search) q.set('search', params.search);
    if (params.limit) q.set('limit', String(params.limit));
    const s = q.toString();
    return api.get<WikiUserX[]>(`/wiki/admin/users${s ? `?${s}` : ''}`);
  },
};

export const notifPrefsApi = {
  get: () => api.get<WikiNotifPrefsX>('/wiki/notifications/prefs'),
  setChannels: (channels: NotifyChannels) => api.patch<{ ok: true }>('/wiki/notifications/prefs', { channels }),
};
